/**
 * MyJobPosts — recruiter's list of created jobs.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/Skeleton";
import { Briefcase, Plus, MapPin, Calendar, Banknote, ArrowLeft, Sparkles, Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function MyJobPosts() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [boosting, setBoosting] = useState(null);

  useEffect(() => {
    api.get("/marketplace/jobs/mine").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  }, []);

  const boost = async (job_id) => {
    setBoosting(job_id);
    try {
      const { data } = await api.post(`/marketplace/jobs/${job_id}/boost`, { days: 7 });
      setJobs((curr) => curr.map((j) => j.job_id === job_id ? { ...j, boosted: true, boosted_until: data.boosted_until } : j));
      toast.success("Job boosted for 7 days", { description: `${data.coins_spent} TeeBee Coins · Top placement on /jobs` });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_COINS") {
        toast.error(`Need ${detail.required} coins · You have ${detail.balance}`, {
          description: "Recharge from your wallet to boost this job.",
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not boost");
      }
    } finally {
      setBoosting(null);
    }
  };

  return (
    <Layout title="My Job Posts">
      <div className="flex items-center justify-between gap-2 mb-5">
        <Button variant="ghost" size="sm" onClick={() => navigate("/hiring")} className="rounded-full" data-testid="jobs-back">
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back to hiring hub
        </Button>
        <Button className="rounded-full" onClick={() => navigate("/hiring/jobs/new")} data-testid="jobs-create">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> New job post
        </Button>
      </div>

      {!jobs ? (
        <div className="space-y-2"><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
      ) : jobs.length === 0 ? (
        <div className="tb-card p-10 text-center" data-testid="jobs-empty">
          <span className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-dashed border-border mb-3">
            <Briefcase className="h-6 w-6 text-muted-foreground" />
          </span>
          <p className="font-display text-lg font-medium">No job posts yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Post your first role to start receiving applications via TeeBee.</p>
          <Button className="rounded-full mt-4" onClick={() => navigate("/hiring/jobs/new")}><Plus className="h-3.5 w-3.5 mr-1.5" /> Post a job</Button>
        </div>
      ) : (
        <div className="space-y-3 tb-stagger">
          {jobs.map((j) => (
            <div key={j.job_id} className="tb-card-interactive p-4" data-testid={`job-row-${j.job_id}`}>
              <div className="flex items-start gap-3">
                <span className="h-9 w-9 rounded-xl tb-chip-lavender grid place-items-center"><Briefcase className="h-4 w-4" /></span>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-base font-medium tracking-tight">{j.role_title}</p>
                  <p className="text-[12px] text-muted-foreground">{j.org_name} · {j.category || "—"}</p>
                  <ul className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
                    {j.location && <li className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {j.location}</li>}
                    {j.salary_min && j.salary_max && <li className="inline-flex items-center gap-1"><Banknote className="h-3 w-3" /> ₹{j.salary_min.toLocaleString()}–₹{j.salary_max.toLocaleString()}</li>}
                    {j.deadline && <li className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Due {j.deadline.slice(0, 10)}</li>}
                  </ul>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-full ${j.is_active ? "tb-chip-mint" : "tb-chip-lavender"}`}>
                  {j.is_active ? "Live" : "Closed"}
                </span>
                {j.boosted ? (
                  <span className="ml-2 tb-chip-butter rounded-full text-[10px] px-2 py-1 font-bold uppercase tracking-wide inline-flex items-center gap-1" data-testid={`job-boosted-${j.job_id}`}>
                    <Sparkles className="h-2.5 w-2.5" /> Boosted
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full ml-2"
                    disabled={boosting === j.job_id}
                    onClick={() => boost(j.job_id)}
                    data-testid={`job-boost-btn-${j.job_id}`}
                  >
                    {boosting === j.job_id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1 text-amber-500" />}
                    Boost · 50 coins
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
